import React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { formatInr } from "../lib/accountsSettlement";

const PAYEE_CATEGORIES = [
  { value: "INDIVIDUAL_HUF", label: "Individual / HUF (1%)" },
  { value: "OTHER", label: "Company / Firm / Other (2%)" },
];

/**
 * Freight TDS — Act 2025 codes 1023 (Ind/HUF) / 1024 (other).
 * Works with purchase slip or sales freight form via onChange patch.
 */
export default function FreightTdsFields({
  value = {},
  onChange,
  settlement = null,
  disabled = false,
  className = "",
  panKey = "pan_no",
  payeeLabel = "",
}) {
  const pan = value[panKey] ?? value.pan_no ?? "";
  const category = value.tds_payee_category || "";
  const smallExempt = Boolean(value.tds_small_transporter_exempt);
  const manual = Boolean(value.tds_manual_override);
  const tdsPct = value.tds_pct ?? "";

  const patch = (partial) => {
    if (typeof onChange !== "function") return;
    onChange({ ...value, ...partial });
  };

  const panRequired = Boolean(
    settlement?.tds_pan_required
    || settlement?.tds_applicable
    || smallExempt
    || (settlement?.tds_reason_code && settlement.tds_reason_code !== "below_threshold" && settlement.tds_reason_code !== "no_amount"),
  );
  const tdsDetail = settlement?.tds_detail || settlement?.tds_194c || {};
  const reason = settlement?.tds_reason || tdsDetail?.reason || "";
  const tdsAmount = settlement?.tds_amount;
  const appliedPct = settlement?.tds_pct;
  const paymentCode = settlement?.tds_payment_code || tdsDetail?.payment_code || tdsDetail?.section || "";

  return (
    <div className={`rounded-md border border-sky-200 bg-sky-50/40 p-4 space-y-3 ${className}`}>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-sky-950">
          TDS · code 1023 / 1024 (freight · Form 140)
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
          TDS follows who we pay freight to
          {payeeLabel ? <> — <strong>{payeeLabel}</strong></> : null}.
          Income-tax Act 2025: code <strong>1023</strong> Ind/HUF (1%), <strong>1024</strong> others (2%).
          No TDS if single payment ≤ ₹30,000 and FY aggregate ≤ ₹1,00,000.
          20% if PAN not furnished. Small transporters (≤10 carriages) exempt with valid PAN.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
            PAN of payee{panRequired ? " *" : ""}
          </Label>
          <Input
            className="mt-1 h-9 uppercase"
            maxLength={10}
            placeholder="ABCDE1234F"
            disabled={disabled}
            value={pan}
            onChange={(e) => {
              const next = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
              patch({ [panKey]: next, pan_no: next });
            }}
          />
          {panRequired && !String(pan || "").trim() ? (
            <p className="text-[10px] text-amber-800 mt-0.5">
              Enter PAN to apply correct rate or claim small-transporter exemption.
            </p>
          ) : null}
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Payee type
          </Label>
          <select
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-2 text-xs"
            disabled={disabled}
            value={category || "_auto"}
            onChange={(e) => {
              const v = e.target.value === "_auto" ? "" : e.target.value;
              patch({ tds_payee_category: v || undefined });
            }}
          >
            <option value="_auto">Auto from PAN (4th letter)</option>
            {PAYEE_CATEGORIES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <label className="flex items-start gap-2 text-xs cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5"
          disabled={disabled}
          checked={smallExempt}
          onChange={(e) => patch({ tds_small_transporter_exempt: e.target.checked })}
        />
        <span>
          <span className="font-medium">Small transporter exemption (≤10 goods carriages)</span>
          <span className="block text-[10px] text-muted-foreground mt-0.5">
            Code 1023/1024 transporter exemption — requires a valid PAN.
          </span>
        </span>
      </label>

      <label className="flex items-start gap-2 text-xs cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5"
          disabled={disabled}
          checked={manual}
          onChange={(e) => patch({
            tds_manual_override: e.target.checked,
            ...(e.target.checked ? {} : { tds_pct: "" }),
          })}
        />
        <span>
          <span className="font-medium">Manual TDS % override</span>
          <span className="block text-[10px] text-muted-foreground mt-0.5">
            Accounts override. Set 0 to waive. Leave off for automatic code 1023/1024.
          </span>
        </span>
      </label>

      {manual ? (
        <div className="max-w-[10rem]">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">TDS %</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            className="mt-1 h-9"
            disabled={disabled}
            value={tdsPct}
            onChange={(e) => patch({ tds_pct: e.target.value, tds_manual_override: true })}
          />
        </div>
      ) : null}

      {settlement ? (
        <div className="rounded-md bg-card/70 border border-sky-100 px-3 py-2 text-xs space-y-0.5">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">TDS</span>
            <span className="tabular font-medium">
              {formatInr(tdsAmount)}
              {appliedPct != null && Number(appliedPct) > 0 ? (
                <span className="text-muted-foreground font-normal"> ({Number(appliedPct)}%)</span>
              ) : null}
            </span>
          </div>
          {reason ? (
            <p className="text-[10px] text-muted-foreground leading-snug">{reason}</p>
          ) : null}
          {paymentCode ? (
            <p className="text-[10px] text-muted-foreground">
              Payment code: <span className="font-mono">{paymentCode}</span>
            </p>
          ) : null}
          {settlement.fy_aggregate_before != null || tdsDetail?.fy_aggregate_before != null ? (
            <p className="text-[10px] text-muted-foreground">
              FY freight already booked for this PAN (other trips):{" "}
              {formatInr(
                settlement.fy_aggregate_before ?? tdsDetail?.fy_aggregate_before ?? 0,
              )}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
